/**
 * Categorias de convidado com preço especial (crianças, staff, ...).
 *
 * O cliente informa as quantidades no simulador da loja. O valor é ADICIONAL
 * (quantidade × preço por convidado × percentual da categoria) e o rótulo e o
 * percentual são "congelados" no momento da venda, para que o histórico não
 * mude se o fornecedor editar as categorias depois.
 */

import {
  parseSpecialPricingTiers,
  getSpecialPricingExtra,
  type SpecialPricingTier,
} from '@/components/event-simulator/utils/pricingAdjustmentUtils';
import type { CartItem } from '@/utils/cartStorage';

export interface SpecialGuestEntry {
  id: string;
  label: string;
  quantity: number;
  /** Percentual do preço por convidado cobrado por pessoa dessa categoria. */
  percentage: number;
  /** Valor total (R$) cobrado pela categoria. */
  amount: number;
}

export type SpecialGuestsSnapshot = Record<
  string,
  { label: string; quantity: number; percentage: number; amount: number }
>;

const toPositiveInt = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
};

/**
 * Monta o snapshot a ser gravado em `events.special_guests` a partir do carrinho.
 * Retorna `{}` quando o cliente não informou nenhuma categoria.
 */
export const buildSpecialGuestsSnapshot = (
  cartItems: readonly CartItem[] | null | undefined,
  /**
   * Preço por convidado (da tabela, já com tipo de evento/temporada) sobre o qual a categoria paga o percentual.
   * Sem informar, usa `pricePerPerson` do item, que é o TOTAL do item dividido pelos convidados (já com garçom,
   * trocas e o próprio staff): por isso quem grava o pedido passa `getCartItemBasePricePerGuest`.
   */
  basePricePerGuestOf: (item: CartItem) => number = (item) => Number(item.pricePerPerson) || 0,
): SpecialGuestsSnapshot => {
  const snapshot: SpecialGuestsSnapshot = {};
  if (!Array.isArray(cartItems)) return snapshot;

  for (const item of cartItems) {
    const quantities = item?.specialGuests;
    if (!quantities || Object.keys(quantities).length === 0) continue;

    const tiers: SpecialPricingTier[] = parseSpecialPricingTiers(item.specialPricingTiers);
    if (tiers.length === 0) continue;

    const base = basePricePerGuestOf(item);
    for (const tier of tiers) {
      const quantity = toPositiveInt(quantities[tier.id]);
      if (quantity <= 0) continue;
      const existing = snapshot[tier.id];
      // Mesma conta do total do pedido (quantidade x preço x percentual, arredondado uma vez): evita 1 centavo de diferença.
      const amount = getSpecialPricingExtra(base, [tier], { [tier.id]: quantity });
      snapshot[tier.id] = {
        label: tier.label,
        quantity,
        percentage: Number(tier.percentage) || 0,
        amount: Math.round(((existing?.amount || 0) + amount) * 100) / 100,
      };
    }
  }

  return snapshot;
};

/** Lê `events.special_guests` (objeto, string JSON ou mapa simples id -> qtd). */
export const parseSpecialGuestsSnapshot = (raw: unknown): SpecialGuestEntry[] => {
  let data: any = raw;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return [];
    }
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) return [];

  return Object.entries(data as Record<string, any>)
    .map(([id, value]) => {
      // Formato simples (id -> quantidade) usado pelo sessionStorage do simulador.
      if (typeof value === 'number' || typeof value === 'string') {
        const quantity = toPositiveInt(value);
        return quantity > 0
          ? { id, label: id, quantity, percentage: 0, amount: 0 }
          : null;
      }
      if (!value || typeof value !== 'object') return null;
      const quantity = toPositiveInt(value.quantity);
      if (quantity <= 0) return null;
      return {
        id,
        label: String(value.label || '').trim() || 'Convidado especial',
        quantity,
        percentage: Number(value.percentage) || 0,
        amount: Number(value.amount) || 0,
      };
    })
    .filter(Boolean) as SpecialGuestEntry[];
};

/** "Crianças" -> "criança" quando a quantidade é 1. */
const singularize = (label: string): string => {
  const lower = label.trim();
  if (/ções$/i.test(lower)) return lower.replace(/ções$/i, 'ção');
  if (/ças$/i.test(lower)) return lower.replace(/ças$/i, 'ça');
  if (/ões$/i.test(lower)) return lower.replace(/ões$/i, 'ão');
  if (/ais$/i.test(lower)) return lower.replace(/ais$/i, 'al');
  if (/s$/i.test(lower) && lower.length > 3) return lower.replace(/s$/i, '');
  return lower;
};

/** Ex.: "1 criança · 2 staff". Retorna "" quando não há categorias. */
export const formatSpecialGuestsSummary = (raw: unknown): string => {
  const entries = parseSpecialGuestsSnapshot(raw);
  if (entries.length === 0) return '';
  return entries
    .map((e) => {
      const label = e.quantity === 1 ? singularize(e.label) : e.label;
      return `${e.quantity} ${label.toLowerCase()}`;
    })
    .join(' · ');
};

/** Ex.: "22 convidados (1 criança · 2 staff)". */
export const formatGuestsWithSpecial = (
  guests: string | number | null | undefined,
  raw: unknown,
  unit = 'convidados',
): string => {
  const base = String(guests ?? '').trim();
  const summary = formatSpecialGuestsSummary(raw);
  const head = base ? `${base}${unit ? ` ${unit}` : ''}` : '';
  if (!summary) return head;
  return head ? `${head} (${summary})` : summary;
};

/** Total de pessoas do evento: convidados + crianças/staff (ex.: 37 + 3 staff + 40 crianças = 80). */
export const getTotalPeople = (guests: number | string | null | undefined, raw: unknown): number => {
  const g = Number(String(guests ?? '').replace(/\D/g, '')) || 0;
  return g + getSpecialGuestsCount(raw);
};

/**
 * Pessoas de um evento da lista (card do Kanban, agenda): convidados + crianças/staff. Os convidados vêm de `guest_total`
 * (já com convidados adicionais) ou, na falta, da contagem do fornecedor.
 */
export const getEventPeopleCount = (event: {
  guest_total?: unknown;
  supplierGuestCount?: unknown;
  guest_count?: unknown;
  special_guests?: unknown;
} | null | undefined): number => {
  const guests = Number(event?.guest_total) || Number(event?.supplierGuestCount) || Number(event?.guest_count) || 0;
  // Sem convidados informados, crianças/staff sozinhos não formam um evento (mesma regra dos adicionais por pessoa).
  return guests > 0 ? getTotalPeople(guests, event?.special_guests) : 0;
};

/** "37 convidados · 3 staff · 40 crianças" ("" quando não há crianças/staff: aí o total já é o número de convidados). */
export const formatPeopleBreakdown = (guests: number | string | null | undefined, raw: unknown): string => {
  const summary = formatSpecialGuestsSummary(raw);
  if (!summary) return '';
  const g = Number(String(guests ?? '').replace(/\D/g, '')) || 0;
  return `${g} ${g === 1 ? 'convidado' : 'convidados'} · ${summary}`;
};

/** Sufixo pronto para colar depois de um número já formatado: " (1 criança)". */
export const formatSpecialGuestsSuffix = (raw: unknown): string => {
  const summary = formatSpecialGuestsSummary(raw);
  return summary ? ` (${summary})` : '';
};

/** Soma das quantidades das categorias especiais (ex.: 1 criança + 1 staff = 2). */
export const getSpecialGuestsCount = (raw: unknown): number =>
  parseSpecialGuestsSnapshot(raw).reduce((sum, e) => sum + e.quantity, 0);

/**
 * Pessoas que contam nos adicionais cobrados "por pessoa" (ex.: garçom R$ 15/pessoa): os convidados + as categorias
 * especiais (crianças, staff...). Antes só os convidados entravam: 20 convidados + 3 staff cobravam o garçom de 20, não de 23.
 * Sem convidados informados, as categorias especiais sozinhas não geram cobrança.
 */
export const getExtrasHeadcount = (guestCount: number, specialGuests: unknown): number =>
  guestCount > 0 ? guestCount + getSpecialGuestsCount(specialGuests) : Math.max(guestCount, 0);

/** Quantidades de crianças/staff que o cliente informou no simulador da loja (sessão do navegador). */
export const readSessionSpecialGuestsCount = (): number => {
  try {
    return getSpecialGuestsCount(JSON.parse(sessionStorage.getItem('ameup_special_guests') || '{}'));
  } catch {
    return 0;
  }
};

/**
 * Monta um snapshot leve (id -> label/quantidade) a partir das categorias
 * configuradas pelo fornecedor + as quantidades informadas pelo cliente.
 * Usado apenas para EXIBIÇÃO (loja, carrinho), onde não há valor congelado.
 */
export const buildSpecialGuestsDisplay = (
  tiers: ReadonlyArray<{ id: string; label: string; percentage?: number }> | null | undefined,
  quantities: Record<string, number | string> | null | undefined,
): SpecialGuestsSnapshot => {
  const snapshot: SpecialGuestsSnapshot = {};
  if (!quantities || typeof quantities !== 'object') return snapshot;

  for (const [id, rawQty] of Object.entries(quantities)) {
    const quantity = toPositiveInt(rawQty);
    if (quantity <= 0) continue;
    const tier = Array.isArray(tiers) ? tiers.find((t) => t.id === id) : undefined;
    snapshot[id] = {
      label: String(tier?.label || '').trim() || 'Convidado especial',
      quantity,
      percentage: Number(tier?.percentage) || 0,
      amount: 0,
    };
  }

  return snapshot;
};

